"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { CircleCheck, Trophy, Shield, Target, Activity, User, Users, Smile, ArrowRight } from "lucide-react";
const programs = [{
  id: 1,
  title: "Montgomery County",
  description: "Expert roof repair, replacement, siding, and gutters for homeowners across Montgomery County.",
  difficulty: "Maryland",
  priceText: "Free consultation included",
  iconName: "Shield"
}, {
  id: 2,
  title: "Frederick County",
  description: "Dedicated roof repair, replacement, siding, and gutters for Frederick residents.",
  difficulty: "Maryland",
  priceText: "Free Estimates",
  iconName: "Trophy"
}, {
  id: 3,
  title: "Howard County",
  description: "Premium roof repair, replacement, siding, and gutters tailored for Howard County homes.",
  difficulty: "Maryland",
  priceText: "Free Estimates",
  iconName: "Target"
}, {
  id: 4,
  title: "Prince George's County",
  description: "Reliable roof repair, replacement, siding, and gutters for PG County communities.",
  difficulty: "Maryland",
  priceText: "Free Estimates",
  iconName: "Activity"
}, {
  id: 5,
  title: "Northern Virginia",
  description: "Comprehensive roof repair, replacement, siding, and gutters for Northern Virginia homeowners.",
  difficulty: "Virginia",
  priceText: "Free Estimates",
  iconName: "User"
}];
const programFeatures = [{
  programId: 1,
  type: "include",
  text: "Full Roof Inspections"
}, {
  programId: 1,
  type: "include",
  text: "Local Permit Handling"
}, {
  programId: 1,
  type: "benefit",
  text: "Bethesda & Rockville"
}, {
  programId: 1,
  type: "benefit",
  text: "Silver Spring & Gaithersburg"
}, {
  programId: 2,
  type: "include",
  text: "Storm Damage Specialists"
}, {
  programId: 2,
  type: "include",
  text: "Insurance Claim Support"
}, {
  programId: 2,
  type: "benefit",
  text: "Frederick City & Urbana"
}, {
  programId: 2,
  type: "benefit",
  text: "Mount Airy & Middletown"
}, {
  programId: 3,
  type: "include",
  text: "Seamless Gutter Systems"
}, {
  programId: 3,
  type: "include",
  text: "Vinyl & Fiber Siding"
}, {
  programId: 3,
  type: "benefit",
  text: "Columbia & Ellicott City"
}, {
  programId: 3,
  type: "benefit",
  text: "Clarksville & Laurel"
}, {
  programId: 4,
  type: "include",
  text: "Residential Roof Repair"
}, {
  programId: 4,
  type: "include",
  text: "Exterior Trim Work"
}, {
  programId: 4,
  type: "benefit",
  text: "Bowie & Upper Marlboro"
}, {
  programId: 4,
  type: "benefit",
  text: "Hyattsville & Laurel"
}, {
  programId: 5,
  type: "include",
  text: "High-End Shingle Options"
}, {
  programId: 5,
  type: "include",
  text: "Custom Exterior Design"
}, {
  programId: 5,
  type: "benefit",
  text: "Arlington & Alexandria"
}, {
  programId: 5,
  type: "benefit",
  text: "Fairfax & Loudoun County"
}];
const getIcon = (name: string) => {
  switch (name) {
    case "Trophy":
      return <Trophy className="size-5 text-primary" />;
    case "Shield":
      return <Shield className="size-5 text-primary" />;
    case "Target":
      return <Target className="size-5 text-primary" />;
    case "Activity":
      return <Activity className="size-5 text-primary" />;
    case "User":
      return <User className="size-5 text-primary" />;
    case "Users":
      return <Users className="size-5 text-primary" />;
    case "Smile":
      return <Smile className="size-5 text-primary" />;
    default:
      return <Activity className="size-5 text-primary" />;
  }
};
export default function PrimaryServiceAreasGrid() {
  return <section data-section-id="259527" id="primary-service-areas-grid" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 text-center max-w-3xl mx-auto">
          <h2 className="font-default text-3xl md:text-5xl font-bold text-primary mb-4">
            Roofing Contractors & Exterior Services Across Maryland & Northern Virginia
          </h2>
          <p className="text-muted-foreground text-lg font-default">
            Eagle Exteriors is the leading choice for Roofing Contractors & Exterior Services Across Maryland & Northern Virginia. Our local expertise ensures your roofing, siding, and gutter projects meet the highest standards of quality and durability.
          </p>
          <p className="mt-4 text-sm font-default text-muted-foreground">
            Explore individual services: <Link to="/RoofRepairPage" className="text-primary hover:underline">Roof Repair</Link> | <Link to="/RoofReplacementPage" className="text-primary hover:underline">Roof Replacement</Link> | <Link to="/SidingReplacementPage" className="text-primary hover:underline">Siding</Link> | <Link to="/GutterInstallationPage" className="text-primary hover:underline">Gutters</Link> | <Link to="/StormDamageRepairPage" className="text-primary hover:underline">Storm Damage</Link>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 lg:gap-8">
          {programs.map((program, i) => {
          const programIncludes = programFeatures.filter(f => f.programId === program.id && f.type === "include");
          const programBenefits = programFeatures.filter(f => f.programId === program.id && f.type === "benefit");
          return <Card key={program.id} data-index={i} className="flex flex-col h-full shadow-md hover:shadow-xl transition-all duration-300 border-primary/20 rounded-md bg-card">
                <CardHeader className="pb-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="p-2 bg-primary/10 rounded-md">
                      {getIcon(program.iconName)}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge variant="secondary" className="font-medium bg-secondary text-secondary-foreground rounded-md">
                        {program.difficulty}
                      </Badge>

                    </div>
                  </div>
                  <CardTitle className="font-default text-xl font-bold text-primary mb-2">{program.title}</CardTitle>
                  <CardDescription className="text-sm text-muted-foreground min-h-[40px] font-default">
                    {program.description}
                  </CardDescription>
                  
                </CardHeader>
                
                <CardContent className="flex-1 flex flex-col gap-4 pb-6">
                  {programIncludes.length > 0 && <div className="space-y-2">
                      <h4 className="font-default text-xs font-bold uppercase tracking-wider text-primary">Services:</h4>
                      <ul className="space-y-1.5">
                        {programIncludes.map((feature, j) => <li key={j} data-index={j} className="flex items-start gap-2 text-sm text-muted-foreground font-default">
                            <CircleCheck className="size-4 text-secondary shrink-0 mt-0.5" />
                            <span>{feature.text}</span>
                          </li>)}
                      </ul>
                    </div>}

                  {programIncludes.length > 0 && programBenefits.length > 0 && <Separator className="my-2 bg-border" />}

                  {programBenefits.length > 0 && <div className="space-y-2">
                      <h4 className="font-default text-xs font-bold uppercase tracking-wider text-primary">Key Cities:</h4>
                      <ul className="space-y-1.5">
                        {programBenefits.map((feature, j) => <li key={j} data-index={j} className="flex items-start gap-2 text-sm text-muted-foreground font-default">
                            <CircleCheck className="size-4 text-secondary shrink-0 mt-0.5" />
                            <span>{feature.text}</span>
                          </li>)}
                      </ul>
                    </div>}
              <div className="mt-auto pt-4">
                    <Table>
                      <TableBody>
                        <TableRow className="hover:bg-transparent border-b-0">
                        <TableCell className="p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px] px-0 py-2 text-right text-secondary font-bold font-default !text-xs"><p className="text-xs font-bold text-secondary mt-2 font-default">
                    Free consultation included
                  </p></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                  
                </CardContent>

                <CardFooter className="pt-0">
                  <Button asChild className="w-full group bg-primary hover:bg-primary/90 text-primary-foreground rounded-md" size="lg">
                    <Link to="/ContactPage">
                      Schedule Your Estimate 
                    </Link>
                  </Button>
                </CardFooter>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}